import type { ReactNode } from "react";

import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CheckNotchedIcon,
  ClockIcon,
  CloseSVGIcon,
  CreditCardIcon,
  LockIcon,
  MobilePhoneIcon,
} from "@braintrust/braintrust-ui-components/Icons";

import styles from "./styles.module.scss";

type VerifyIdentityModalTopSectionProps = {
  onSkip: () => void;
  title: ReactNode;
  subtitle: string;
};

export const VerifyIdentityModalTopSection = ({
  onSkip,
  title,
  subtitle,
}: VerifyIdentityModalTopSectionProps) => {
  return (
    <Stack
      className={styles.topSection}
      sx={{
        position: "relative",
        background:
          "linear-gradient(114.45deg, var(--medium-violet) 0%, #D5B5F5 100%)",
        px: { xs: 3, sm: 4 },
        pt: { xs: 3, sm: 4 },
        pb: 3,
      }}
    >
      <IconButton
        aria-label="close modal"
        variant="transparent"
        size="small"
        sx={{
          position: "absolute",
          top: "18px",
          right: "18px",
          color: "var(--grey-2) !important",
          svg: { margin: 0 },
          path: { stroke: "currentColor" },
        }}
        onClick={onSkip}
      >
        <CloseSVGIcon />
      </IconButton>
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          borderRadius: 4,
          backgroundColor: "var(--white)",
          boxShadow: "var(--elevation-4)",
          width: "56px",
          height: "56px",
          mb: 3,
        }}
      >
        <LockIcon sx={{ fontSize: "26px", color: "var(--dark-violet)" }} />
      </Box>
      <Typography
        component="p"
        className={styles.dotPrefix}
        sx={{
          fontSize: "11px !important",
          color: "var(--dark-violet)",
          letterSpacing: "1px !important",
          ml: 1,
          mb: "20px",
        }}
        fontWeight={500}
      >
        {subtitle}
      </Typography>
      <Typography component="h2" variant="title" size="small" fontWeight={500}>
        {title}
      </Typography>
    </Stack>
  );
};

type VerifyIdentityModalBottomSectionProps = {
  onVerifyIdentityClick: () => void;
};

export const VerifyIdentityModalBottomSection = ({
  onVerifyIdentityClick,
}: VerifyIdentityModalBottomSectionProps) => {
  return (
    <Stack sx={{ p: { xs: 3, sm: 4 } }}>
      <Typography
        component="p"
        variant="paragraph"
        size="small"
        sx={{ mb: 4, fontSize: "14px !important", maxWidth: "380px" }}
      >
        Clients only hire verified talent on Braintrust — it's how we maintain a
        high-quality, trusted network.
      </Typography>

      <Stack
        sx={{
          gap: 1.5,
          px: 2,
          py: 3,
          backgroundColor: "var(--soft-violet)",
          border: "1px solid var(--medium-violet)",
          borderRadius: 3,
          m: 0,
          mb: 3,
        }}
        component="ul"
      >
        <BottomSectionItem
          icon={
            <ClockIcon sx={{ color: "var(--dark-violet)", fontSize: "14px" }} />
          }
          content={
            <>
              Takes about <strong>30 seconds</strong> to complete
            </>
          }
        />
        <BottomSectionItem
          icon={
            <MobilePhoneIcon
              sx={{ color: "var(--dark-violet)", fontSize: "14px" }}
            />
          }
          content={
            <>
              Have your <strong>mobile phone</strong> ready
            </>
          }
        />
        <BottomSectionItem
          icon={
            <CheckNotchedIcon
              sx={{ color: "var(--positive-2)", fontSize: "14px" }}
            />
          }
          content={
            <>
              <strong>One time only</strong> — you'll never need to do this
              again
            </>
          }
        />
        <BottomSectionItem
          icon={
            <CreditCardIcon sx={{ color: "var(--info-2)", fontSize: "14px" }} />
          }
          content={
            <>
              Have your <strong>government-issued ID</strong> ready
            </>
          }
        />
      </Stack>

      <Stack direction="row" sx={{ gap: "10px", mb: 1.5 }}>
        <Button
          variant="dark-violet"
          shape="squared"
          onClick={onVerifyIdentityClick}
          sx={{
            flex: 1,
            boxShadow: "0px 4px 12px 0px #5D197440 !important",
          }}
          size="medium"
        >
          Verify identity
        </Button>
      </Stack>
      <Typography
        component="p"
        size="small"
        sx={{ fontSize: "12px !important", textAlign: "center" }}
        color="grey-2"
      >
        <LockIcon
          sx={{
            color: "inherit",
            fontSize: "11px",
            verticalAlign: "text-top",
          }}
        />{" "}
        Your data is encrypted and never shared with clients
      </Typography>
    </Stack>
  );
};

const BottomSectionItem = ({
  icon,
  content,
}: {
  icon: ReactNode;
  content: ReactNode;
}) => {
  return (
    <Stack
      component="li"
      direction="row"
      sx={{ gap: 1.5, alignItems: "center" }}
    >
      <Box
        sx={{
          width: "28px",
          height: "28px",
          backgroundColor: "var(--white)",
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          border: "1px solid var(--medium-violet)",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography component="p" size="small">
        {content}
      </Typography>
    </Stack>
  );
};
